import React, { useState, useEffect, useRef } from 'react';
import { properties } from '../../properties';
import apiFetch from '../../auth/apiFetch';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { getAffiliates, getDojosNames, getGrades, getStates } from '../../view/HTTPService';
import { isValidE164, formatPhoneForDisplay, e164ToMasked, stripMask } from '../../utils/phone';
import { Toast } from 'primereact/toast';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { InputMask } from 'primereact/inputmask';
import { Dropdown } from 'primereact/dropdown';
import { Dialog } from 'primereact/dialog';
import { Fieldset } from 'primereact/fieldset';
import { useTranslation } from 'react-i18next';

import 'primereact/resources/primereact.min.css';
import 'primeicons/primeicons.css';
import 'primeflex/primeflex.css';

// Novo: dialog decomposto em tabs
import { AffiliateFormDialog } from './AffiliateFormDialog';

function LicenseFormFields({
    t,
    licenseClass, setLicenseClass,
    licenseRegistrationId, setLicenseRegistrationId,
    licenseAquisitionYear, setLicenseAquisitionYear,
    licenseValid, setLicenseValid,
}) {
    return (
        <div className="field">
            <Fieldset>
                <div className="formgrid grid license">
                    <div className="field input text col-2">
                        <label htmlFor="licenseClass">{t('msg.jkaLicenseClass')}</label>
                        <InputText id="licenseClass" placeholder="A" value={licenseClass}
                            onChange={(e) => setLicenseClass(e.target.value)} maxLength={1} />
                    </div>
                    <div className="field input text col-4">
                        <label htmlFor="licenseRegistrationId">{t('msg.jkaLicenseRegistrationId')}</label>
                        <InputText id="licenseRegistrationId" placeholder="99A9999"
                            value={licenseRegistrationId}
                            onChange={(e) => setLicenseRegistrationId(e.target.value)} maxLength={7} />
                    </div>
                    <div className="field input text col-3">
                        <label htmlFor="licenseAquisitionDate">{t('msg.jkaLicenseAquisitionYear')}</label>
                        <InputText id="licenseAquisitionDate" placeholder="31/12/2023"
                            value={licenseAquisitionYear}
                            onChange={(e) => setLicenseAquisitionYear(e.target.value)} />
                    </div>
                    <div className="field input text col-3">
                        <label htmlFor="licenseValidDate">{t('msg.jkaLicenseValid')}</label>
                        <InputText id="licenseValidDate" placeholder="31/12/2023"
                            value={licenseValid}
                            onChange={(e) => setLicenseValid(e.target.value)} />
                    </div>
                </div>
            </Fieldset>
        </div>
    );
}

const emptyAffiliate = {
    id: '',
    name: '',
    cpf: '',
    rg: '',
    birthDate: '',
    date: '',
    jkaRegister: '',
    jkaId: '',
    jkaJpRecord: '',
    passport: '',
    jkaJpId: '',
    state: { id: '', name: '' },
    status: false,
    active: false,
    jka_affiliate: false,
    affiliateAddress: {
        addressComplement: '',
        city: '',
        neighborhood: '',
        state: '',
        street: '',
        streetNumber: '',
        zipCode: '',
    },
    contacts: {
        email1: '', email2: '', mobile1: '', mobile2: '', phone1: '', phone2: '',
    },
    gym: { id: '', name: '' },
    grade: { id: '', belt: '', level: '' },
    genericGrade: '',
    socialMedia: {
        facebookId: '', instagramId: '', site: '', twitterId: '',
    },
    note: '',
    occupation: '',
    affiliateAnnuities: [],
    judgeLicenses: [],
    instructorLicenses: [],
    examinerLicenses: [],
    danExams: [],
};

/**
 * AffiliateList - lista de afiliados com dialog decomposto em tabs.
 *
 * Props (todas opcionais - default é lista simples de todos os afiliados):
 *   titleKey        - chave i18n do título (default: 'msg.jkaAffiliates')
 *   fetchAffiliates - função async que retorna a lista inicial (default: getAffiliates).
 *                     Passe null pra NÃO carregar nada (útil em telas de busca puras).
 *   searchByName    - se passado (função), mostra input + botão "Buscar" no header (busca por nome).
 *   searchByJkaId   - se passado (função), mostra input + botão "Buscar" no header (busca por JKA ID).
 *   filterByState   - se passado (função), mostra dropdown de estados no header.
 */
function AffiliateList({
    titleKey = 'msg.jkaAffiliates',
    fetchAffiliates = getAffiliates,
    searchByName = null,
    searchByJkaId = null,
    filterByState = null,
} = {}) {
    const { t } = useTranslation();

    // ==================== DADOS PRINCIPAIS ====================
    const [affiliates, setAffiliates] = useState(null);
    const [affiliate, setAffiliate] = useState(emptyAffiliate);
    const [states, setStates] = useState(null);
    const [dojos, setDojos] = useState(null);
    const [grades, setGrades] = useState(null);

    // ==================== ESTADOS DE BUSCA (opcionais) ====================
    const [searchValue, setSearchValue] = useState('');
    const [selectedFilterState, setSelectedFilterState] = useState(null);

    useEffect(() => {
        getDojosNames().then(data => setDojos(data));
        getStates().then(data => setStates(data));
        getGrades().then(data => setGrades(data));
        // Só carrega a lista inicial se fetchAffiliates foi passado (não é null)
        if (fetchAffiliates) {
            fetchAffiliates().then(data => setAffiliates(data));
        }
    }, [fetchAffiliates]);

    // ==================== HANDLERS DE BUSCA ====================
    const handleSearchByName = () => {
        if (searchByName && searchValue.trim()) {
            searchByName(searchValue).then(data => setAffiliates(data));
        }
    };

    const handleSearchByJkaId = () => {
        if (searchByJkaId && searchValue.trim()) {
            searchByJkaId(searchValue).then(data => setAffiliates(data));
        } else if (searchByJkaId) {
            toast.current.show({
                severity: 'warn',
                summary: 'Atenção',
                detail: 'Digite um JKA ID para buscar',
                life: 3000,
            });
        }
    };

    const handleFilterStateChange = (e) => {
        setSelectedFilterState(e.value);
        if (filterByState && e.value) {
            filterByState(e.value.id).then(data => setAffiliates(data));
        }
    };

    const handleSearchKeyPress = (e) => {
        if (e.key === 'Enter') {
            if (searchByName) handleSearchByName();
            else if (searchByJkaId) handleSearchByJkaId();
        }
    };

    // ==================== OPÇÕES DE DROPDOWNS ====================
    const statusPayment = [
        { name: 'Pendente', value: false },
        { name: 'Paga', value: true },
    ];
    const statusActive = [
        { name: 'Inativo', value: false },
        { name: 'Ativo', value: true },
    ];

    // ==================== ESTADOS DE UI ====================
    const [activeIndex, setActiveIndex] = useState(0);
    const [affiliateDialog, setAffiliateDialog] = useState(false);
    const [deleteAffiliateDialog, setDeleteAffiliateDialog] = useState(false);
    const [deleteAffiliatesDialog, setDeleteAffiliatesDialog] = useState(false);
    const [selectedAffiliates, setSelectedAffiliates] = useState(null);
    const [submitted, setSubmitted] = useState(false);
    const [loadingEdit, setLoadingEdit] = useState(false);
    const [globalFilter, setGlobalFilter] = useState(null);
    const [idToDelete, setIdToDelete] = useState(0);
    const [affiliateApiError, setAffiliateApiError] = useState('');
    const toast = useRef(null);
    const dt = useRef(null);

    // Dropdowns selecionados
    const [selectedDojo, setSelectedDojo] = useState(null);
    const [selectedState, setSelectedState] = useState(null);
    const [selectedGrade, setSelectedGrade] = useState(null);
    const [selectedPaid, setSelectedPaid] = useState(false);
    const [selectedActive, setSelectedActive] = useState(false);

    // ==================== ANUIDADES ====================
    const [annuityDialog, setAnnuityDialog] = useState(false);
    const [editAnnuityDialog, setEditAnnuityDialog] = useState(false);
    const [deleteAnnuityDialog, setDeleteAnnuityDialog] = useState(false);
    const [annuityId, setAnnuityId] = useState('');
    const [annuityYear, setAnnuityYear] = useState('');
    const [paymentDate, setPaymentDate] = useState('');
    const [value, setValue] = useState('');
    const [jkabrCardDate, setJkabrCardDate] = useState('');

    // ==================== DAN EXAMS ====================
    const [danExamDialog, setDanExamDialog] = useState(false);
    const [editDanExamDialog, setEditDanExamDialog] = useState(false);
    const [deleteDanExamDialog, setDeleteDanExamDialog] = useState(false);
    const [danExamId, setDanExamId] = useState('');
    const [danExamDanId, setDanExamDanId] = useState('');
    const [danExamRecordDate, setDanExamRecordDate] = useState('');
    const [danExamRecord, setDanExamRecord] = useState('');

    // ==================== LICENSES (compartilhado entre judge/instructor/examiner) ====================
    const [licenseId, setLicenseId] = useState('');
    const [licenseClass, setLicenseClass] = useState('');
    const [licenseRegistrationId, setLicenseRegistrationId] = useState('');
    const [licenseAquisitionYear, setLicenseAquisitionYear] = useState('');
    const [licenseValid, setLicenseValid] = useState('');

    // Judge
    const [judgeLicenseDialog, setJudgeLicenseDialog] = useState(false);
    const [editJudgeLicenseDialog, setEditJudgeLicenseDialog] = useState(false);
    const [deleteJudgeLicenseDialog, setDeleteJudgeLicenseDialog] = useState(false);

    // Instructor
    const [instructorLicenseDialog, setInstructorLicenseDialog] = useState(false);
    const [editInstructorLicenseDialog, setEditInstructorLicenseDialog] = useState(false);
    const [deleteInstructorLicenseDialog, setDeleteInstructorLicenseDialog] = useState(false);

    // Examiner
    const [examinerLicenseDialog, setExaminerLicenseDialog] = useState(false);
    const [editExaminerLicenseDialog, setEditExaminerLicenseDialog] = useState(false);
    const [deleteExaminerLicenseDialog, setDeleteExaminerLicenseDialog] = useState(false);

    // ==================== HELPERS ====================
    function clearLicenseData() {
        setLicenseClass('');
        setLicenseRegistrationId('');
        setLicenseAquisitionYear('');
        setLicenseValid('');
    }

    function clearDanExamData() {
        setDanExamId('');
        setDanExamDanId('');
        setDanExamRecordDate('');
        setDanExamRecord('');
    }

    function clearAnnuityData() { }

    const findIndexById = (id) => affiliates.findIndex(a => a.id === id);

    // ==================== HANDLERS DE DROPDOWN ====================
    const onDojoChange = (e) => {
        setSelectedDojo(e.value);
        setAffiliate({ ...affiliate, gym: e.value });
    };
    const onStateChange = (e) => {
        setSelectedState(e.value);
        setAffiliate({ ...affiliate, state: e.value });
    };
    const onPaidChange = (e) => {
        setSelectedPaid(e.value);
        setAffiliate({ ...affiliate, status: e.value });
    };
    const onActiveChange = (e) => {
        setSelectedActive(e.value);
        setAffiliate({ ...affiliate, active: e.value });
    };
    const onGradeChange = (e) => {
        setSelectedGrade(e.value);
        setAffiliate({ ...affiliate, grade: e.value });
    };

    // ==================== onInputChange (aceita dot-notation) ====================
    const onInputChange = (e, name) => {
        const val = (e.target && e.target.value) || '';
        const _affiliate = { ...affiliate };
        if (name.includes('.')) {
            const [first, second] = name.split('.');
            _affiliate[first][second] = val;
        } else {
            _affiliate[name] = val;
        }
        setAffiliate(_affiliate);
    };

    // ==================== CRUD AFFILIATE ====================
    async function putAffiliate(a) {
        const res = await apiFetch(properties.baseURL + properties.affiliatesPath + '/' + a.id, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(a),
        });
        if (!res.ok) {
            const body = await res.json().catch(() => ({}));
            throw new Error(body.error || `Erro ${res.status} – ${res.statusText}`);
        }
        return res.json();
    }

    async function postAffiliate(a) {
        const res = await apiFetch(properties.baseURL + properties.affiliatesPath, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(a),
        });
        if (!res.ok) {
            const body = await res.json().catch(() => ({}));
            throw new Error(body.error || `Erro ${res.status} – ${res.statusText}`);
        }
        return res.json();
    }

    const openNew = () => {
        setAffiliate(emptyAffiliate);
        setSubmitted(false);
        setAffiliateApiError('');
        setAffiliateDialog(true);
    };

    const hideDialog = () => {
        setSubmitted(false);
        setAffiliateApiError('');
        setAffiliateDialog(false);
    };

    const saveAffiliate = async () => {
        setSubmitted(true);
        setAffiliateApiError('');

        const missing = [];
        if (!affiliate.name?.trim()) missing.push('Nome');
        if (!affiliate.rg?.trim()) missing.push('RG');
        if (!affiliate.cpf?.trim()) missing.push('CPF');
        if (!affiliate.birthDate?.trim()) missing.push('Data de nascimento');
        if (!selectedDojo) missing.push('Dojo');
        if (!selectedGrade) missing.push('Graduação');
        if (!selectedState) missing.push('Estadual');

        if (missing.length > 0) {
            toast.current.show({
                severity: 'warn',
                summary: 'Campos obrigatórios',
                detail: `Preencha: ${missing.join(', ')}`,
                life: 5000,
            });
            return;
        }

        // Remove máscara dos campos de telefone antes de enviar
        const contacts = { ...affiliate.contacts };
        ['mobile1', 'mobile2', 'phone1', 'phone2'].forEach(f => {
            if (contacts[f]) contacts[f] = stripMask(contacts[f]);
        });

        affiliate.gym.id = selectedDojo.id;
        affiliate.grade.id = selectedGrade.id;
        const _affiliate = { ...affiliate, contacts };

        try {
            if (affiliate.id) {
                await putAffiliate(_affiliate);
                const _affiliates = [...affiliates];
                const index = findIndexById(affiliate.id);
                _affiliates[index] = _affiliate;
                setAffiliates(_affiliates);
                toast.current.show({ severity: 'success', summary: t('msg.jkaSuccessful'), detail: t('msg.jkaAffiliateUpdated'), life: 3000 });
            } else {
                _affiliate.id = 0;
                _affiliate.image = 'product-placeholder.svg';
                await postAffiliate(_affiliate);
                const _affiliates = [...affiliates];
                _affiliates.push(_affiliate);
                setAffiliates(_affiliates);
                toast.current.show({ severity: 'success', summary: t('msg.jkaSuccessful'), detail: t('msg.jkaAffiliateCreated'), life: 3000 });
            }
            setAffiliateDialog(false);
            setAffiliate(emptyAffiliate);
        } catch (err) {
            setAffiliateApiError(err.message);
        }
    };

    const editAffiliate = async (rowData) => {
        setLoadingEdit(true);
        try {
            const res = await apiFetch(properties.baseURL + properties.affiliatesPath + '/' + rowData.id);
            if (!res.ok) throw new Error(`Erro ${res.status}`);
            const detail = await res.json();
            setAffiliate({
                ...detail,
                contacts: {
                    ...detail.contacts,
                    mobile1: e164ToMasked(detail.contacts.mobile1, 'mobile'),
                    mobile2: e164ToMasked(detail.contacts.mobile2, 'mobile'),
                    phone1: e164ToMasked(detail.contacts.phone1, 'phone'),
                    phone2: e164ToMasked(detail.contacts.phone2, 'phone'),
                },
            });
            setSelectedDojo({ id: detail.gym.id, name: detail.gym.name });
            setSelectedGrade({ id: detail.grade.id, level: detail.grade.level, belt: detail.grade.belt });
            setSelectedState({ id: detail.state.id, name: detail.state.name });
            setSelectedPaid(detail.status);
            setSelectedActive(detail.active);
            setAffiliateApiError('');
            setAffiliateDialog(true);
        } catch (err) {
            toast.current.show({
                severity: 'error',
                summary: 'Erro',
                detail: 'Não foi possível carregar os dados do associado.',
                life: 4000,
            });
        } finally {
            setLoadingEdit(false);
        }
    };

    const confirmDeleteAffiliate = (a) => {
        setAffiliate(a);
        setDeleteAffiliateDialog(true);
    };

    const deleteAffiliate = () => {
        const _affiliates = affiliates.filter(v => v.id !== affiliate.id);
        setAffiliates(_affiliates);
        apiFetch(properties.baseURL + properties.affiliatesPath + `/${affiliate.id}`, { method: 'DELETE' });
        setDeleteAffiliateDialog(false);
        setAffiliate(emptyAffiliate);
        toast.current.show({ severity: 'success', summary: 'Successful', detail: `Associado ${affiliate.name} deletado`, life: 3000 });
    };

    const confirmDeleteSelected = () => setDeleteAffiliatesDialog(true);
    const deleteSelectedAffiliates = () => {
        setAffiliates(affiliates.filter(v => !selectedAffiliates.includes(v)));
        setDeleteAffiliatesDialog(false);
        setSelectedAffiliates(null);
    };

    // ==================== ANNUITY: add/edit/delete ====================
    const showAnnuityDialog = () => { clearAnnuityData(); setAnnuityDialog(true); };
    const hideInsertAnnuityDialog = () => { clearAnnuityData(); setAnnuityDialog(false); };
    const hideEditAnnuityDialog = () => { clearAnnuityData(); setEditAnnuityDialog(false); };
    const hideDeleteAnnuityDialog = () => setDeleteAnnuityDialog(false);

    const addAnnuity = () => {
        const _affiliate = { ...affiliate };
        const annuity = { year: annuityYear, paymentDate, jkabrCardDate, value };
        _affiliate.affiliateAnnuities = _affiliate.affiliateAnnuities.concat(annuity);
        setAffiliate(_affiliate);
        toast.current.show({ severity: 'success', summary: 'Successful', detail: t('msg.jkaAffiliateAnnuityAdd'), life: 3000 });
        setAnnuityDialog(false);
    };

    const editAnnuity = (a) => {
        setAnnuityId(a.id);
        setAnnuityYear(a.year);
        setPaymentDate(a.paymentDate);
        setValue(a.value);
        setJkabrCardDate(a.jkabrCardDate);
        setEditAnnuityDialog(true);
    };

    const updateAnnuity = () => {
        const _affiliate = { ...affiliate };
        const idx = _affiliate.affiliateAnnuities.findIndex(o => o.id === annuityId);
        _affiliate.affiliateAnnuities[idx].value = value;
        _affiliate.affiliateAnnuities[idx].year = annuityYear;
        _affiliate.affiliateAnnuities[idx].paymentDate = paymentDate;
        _affiliate.affiliateAnnuities[idx].jkabrCardDate = jkabrCardDate;
        setAffiliate(_affiliate);
        setEditAnnuityDialog(false);
    };

    const confirmDeleteAnnuity = (a) => {
        setIdToDelete(a.id);
        setDeleteAnnuityDialog(true);
    };

    const deleteAnnuity = () => {
        const _affiliate = { ...affiliate };
        const idx = _affiliate.affiliateAnnuities.findIndex(o => o.id === idToDelete);
        delete _affiliate.affiliateAnnuities[idx];
        setAffiliate(_affiliate);
        apiFetch(properties.baseURL + properties.affiliateAnnuitiesPath + `/${idToDelete}`, { method: 'DELETE' });
        setIdToDelete(0);
        setDeleteAnnuityDialog(false);
    };

    // ==================== DAN EXAM: add/edit/delete ====================
    const showDanExamDialog = () => { clearDanExamData(); setDanExamDialog(true); };
    const hideInsertDanExamDialog = () => { clearDanExamData(); setDanExamDialog(false); };
    const hideEditDanExamDialog = () => { clearLicenseData(); setEditDanExamDialog(false); };
    const hideDeleteDanExamDialog = () => setDeleteDanExamDialog(false);

    const addDanExam = () => {
        const _affiliate = { ...affiliate };
        const level = danExamDanId + ' DAN';
        const danExam = { record: danExamRecord, date: danExamRecordDate, danId: danExamDanId };
        _affiliate.danExams = _affiliate.danExams.concat(danExam);
        _affiliate.grade = { id: danExamDanId, level, belt: 'Faixa Preta' };
        setSelectedGrade({ id: danExamDanId, level, belt: 'Faixa Preta' });
        _affiliate.genericGrade = level;
        _affiliate.jka_affiliate = true;
        setAffiliate(_affiliate);
        toast.current.show({ severity: 'success', summary: 'Successful', detail: t('msg.jkaDanExamAdd'), life: 3000 });
        setDanExamDialog(false);
    };

    const editDanExam = (d) => {
        setDanExamId(d.id);
        setDanExamDanId(d.danId);
        setDanExamRecordDate(d.date);
        setDanExamRecord(d.record);
        setEditDanExamDialog(true);
    };

    const updateDanExam = () => {
        const _affiliate = { ...affiliate };
        const idx = _affiliate.danExams.findIndex(o => o.id === danExamId);
        _affiliate.danExams[idx].date = danExamRecordDate;
        _affiliate.danExams[idx].danId = danExamDanId;
        _affiliate.danExams[idx].record = danExamRecord;
        setAffiliate(_affiliate);
        clearDanExamData();
        setEditDanExamDialog(false);
    };

    const confirmDeleteDanExam = (d) => {
        setIdToDelete(d.id);
        setDeleteDanExamDialog(true);
    };

    const deleteDanExam = () => {
        const _affiliate = { ...affiliate };
        const idx = _affiliate.danExams.findIndex(o => o.id === idToDelete);
        delete _affiliate.danExams[idx];
        setAffiliate(_affiliate);
        apiFetch(properties.baseURL + properties.danExamsPath + `/${idToDelete}`, { method: 'DELETE' });
        setIdToDelete(0);
        setDeleteDanExamDialog(false);
    };

    // ==================== LICENSES (judge/instructor/examiner) ====================
    // Judge
    const showJudgeLicenseDialog = () => { clearLicenseData(); setJudgeLicenseDialog(true); };
    const hideInsertJudgeLicenseDialog = () => { clearLicenseData(); setJudgeLicenseDialog(false); };
    const hideEditJudgeLicenseDialog = () => { clearLicenseData(); setEditJudgeLicenseDialog(false); };
    const hideDeleteJudgeLicenseDialog = () => setDeleteJudgeLicenseDialog(false);

    const addJudgeLicense = () => {
        const _affiliate = { ...affiliate };
        const license = { classe: licenseClass, registrationId: licenseRegistrationId, aquisitionDate: licenseAquisitionYear, validDate: licenseValid };
        _affiliate.judgeLicenses = _affiliate.judgeLicenses.concat(license);
        setAffiliate(_affiliate);
        toast.current.show({ severity: 'success', summary: 'Successful', detail: t('msg.jkaLicenseAdd'), life: 3000 });
        clearLicenseData();
        setJudgeLicenseDialog(false);
    };

    const editJudgeLicense = (lic) => {
        setLicenseId(lic.id);
        setLicenseClass(lic.classe);
        setLicenseRegistrationId(lic.registrationId);
        setLicenseAquisitionYear(lic.aquisitionDate);
        setLicenseValid(lic.validDate);
        setEditJudgeLicenseDialog(true);
    };

    const updateJudgeLicense = () => {
        const _affiliate = { ...affiliate };
        const idx = _affiliate.judgeLicenses.findIndex(o => o.id === licenseId);
        _affiliate.judgeLicenses[idx].classe = licenseClass;
        _affiliate.judgeLicenses[idx].registrationId = licenseRegistrationId;
        _affiliate.judgeLicenses[idx].aquisitionDate = licenseAquisitionYear;
        _affiliate.judgeLicenses[idx].validDate = licenseValid;
        setAffiliate(_affiliate);
        clearLicenseData();
        setEditJudgeLicenseDialog(false);
    };

    const confirmDeleteJudgeLicense = (lic) => {
        setIdToDelete(lic.id);
        setDeleteJudgeLicenseDialog(true);
    };

    const deleteJudgeLicense = () => {
        const _affiliate = { ...affiliate };
        const idx = _affiliate.judgeLicenses.findIndex(o => o.id === idToDelete);
        delete _affiliate.judgeLicenses[idx];
        setAffiliate(_affiliate);
        apiFetch(properties.baseURL + properties.judgelicensesPath + `/${idToDelete}`, { method: 'DELETE' });
        setIdToDelete(0);
        setDeleteJudgeLicenseDialog(false);
    };

    // Instructor
    const showInstructorLicenseDialog = () => { clearLicenseData(); setInstructorLicenseDialog(true); };
    const hideInsertInstructorLicenseDialog = () => { clearLicenseData(); setInstructorLicenseDialog(false); };
    const hideEditInstructorLicenseDialog = () => { clearLicenseData(); setEditInstructorLicenseDialog(false); };
    const hideDeleteInstructorLicenseDialog = () => setDeleteInstructorLicenseDialog(false);

    const addInstructorLicense = () => {
        const _affiliate = { ...affiliate };
        const license = { classe: licenseClass, registrationId: licenseRegistrationId, aquisitionDate: licenseAquisitionYear, validDate: licenseValid };
        _affiliate.instructorLicenses = _affiliate.instructorLicenses.concat(license);
        setAffiliate(_affiliate);
        toast.current.show({ severity: 'success', summary: 'Successful', detail: t('msg.jkaLicenseAdd'), life: 3000 });
        setInstructorLicenseDialog(false);
    };

    const editInstructorLicense = (lic) => {
        setLicenseId(lic.id);
        setLicenseClass(lic.classe);
        setLicenseRegistrationId(lic.registrationId);
        setLicenseAquisitionYear(lic.aquisitionDate);
        setLicenseValid(lic.validDate);
        setEditInstructorLicenseDialog(true);
    };

    const updateInstructorLicense = () => {
        const _affiliate = { ...affiliate };
        const idx = _affiliate.instructorLicenses.findIndex(o => o.id === licenseId);
        _affiliate.instructorLicenses[idx].classe = licenseClass;
        _affiliate.instructorLicenses[idx].registrationId = licenseRegistrationId;
        _affiliate.instructorLicenses[idx].aquisitionDate = licenseAquisitionYear;
        _affiliate.instructorLicenses[idx].validDate = licenseValid;
        setAffiliate(_affiliate);
        clearLicenseData();
        setEditInstructorLicenseDialog(false);
    };

    const confirmDeleteInstructorLicense = (lic) => {
        setIdToDelete(lic.id);
        setDeleteInstructorLicenseDialog(true);
    };

    const deleteInstructorLicense = () => {
        const _affiliate = { ...affiliate };
        const idx = _affiliate.instructorLicenses.findIndex(o => o.id === idToDelete);
        delete _affiliate.instructorLicenses[idx];
        setAffiliate(_affiliate);
        apiFetch(properties.baseURL + properties.instructorlicensesPath + `/${idToDelete}`, { method: 'DELETE' });
        setIdToDelete(0);
        setDeleteInstructorLicenseDialog(false);
    };

    // Examiner
    const showExaminerLicenseDialog = () => { clearLicenseData(); setExaminerLicenseDialog(true); };
    const hideInsertExaminerLicenseDialog = () => { clearLicenseData(); setExaminerLicenseDialog(false); };
    const hideEditExaminerLicenseDialog = () => { clearLicenseData(); setEditExaminerLicenseDialog(false); };
    const hideDeleteExaminerLicenseDialog = () => setDeleteExaminerLicenseDialog(false);

    const addExaminerLicense = () => {
        const _affiliate = { ...affiliate };
        const license = { classe: licenseClass, registrationId: licenseRegistrationId, aquisitionDate: licenseAquisitionYear, validDate: licenseValid };
        _affiliate.examinerLicenses = _affiliate.examinerLicenses.concat(license);
        setAffiliate(_affiliate);
        toast.current.show({ severity: 'success', summary: 'Successful', detail: t('msg.jkaLicenseAdd'), life: 3000 });
        setExaminerLicenseDialog(false);
    };

    const editExaminerLicense = (lic) => {
        setLicenseId(lic.id);
        setLicenseClass(lic.classe);
        setLicenseRegistrationId(lic.registrationId);
        setLicenseAquisitionYear(lic.aquisitionDate);
        setLicenseValid(lic.validDate);
        setEditExaminerLicenseDialog(true);
    };

    const updateExaminerLicense = () => {
        const _affiliate = { ...affiliate };
        const idx = _affiliate.examinerLicenses.findIndex(o => o.id === licenseId);
        _affiliate.examinerLicenses[idx].classe = licenseClass;
        _affiliate.examinerLicenses[idx].registrationId = licenseRegistrationId;
        _affiliate.examinerLicenses[idx].aquisitionDate = licenseAquisitionYear;
        _affiliate.examinerLicenses[idx].validDate = licenseValid;
        setAffiliate(_affiliate);
        clearLicenseData();
        setEditExaminerLicenseDialog(false);
    };

    const confirmDeleteExaminerLicense = (lic) => {
        setIdToDelete(lic.id);
        setDeleteExaminerLicenseDialog(true);
    };

    const deleteExaminerLicense = () => {
        const _affiliate = { ...affiliate };
        const idx = _affiliate.examinerLicenses.findIndex(o => o.id === idToDelete);
        delete _affiliate.examinerLicenses[idx];
        setAffiliate(_affiliate);
        apiFetch(properties.baseURL + properties.examinerlicensesPath + `/${idToDelete}`, { method: 'DELETE' });
        setIdToDelete(0);
        setDeleteExaminerLicenseDialog(false);
    };

    // ==================== BODY TEMPLATES DA TABELA ====================
    const phoneBodyTemplate = (value) => {
        if (!value) return '';
        if (isValidE164(value)) return formatPhoneForDisplay(value);
        return (
            <span>
                {value}{' '}
                <i
                    className="pi pi-exclamation-triangle"
                    style={{ color: '#f59e0b' }}
                    title="Telefone não validado"
                />
            </span>
        );
    };

    const statusBodyTemplate = (row) => (
        <span className={`customer-badge ${row.status ? 'status-qualified' : 'status-unqualified'}`}>
            {row.status ? t('msg.jkaPaidAnnuity') : t('msg.jkaUnpaidAnnuity')}
        </span>
    );
    const activeBodyTemplate = (row) => (
        <span className={`customer-badge ${row.active ? 'status-qualified' : 'status-unqualified'}`}>
            {row.active ? t('msg.jkaActive') : t('msg.jkaInactive')}
        </span>
    );
    const actionBodyTemplate = (row) => (
        <>
            <Button icon="pi pi-pencil" loading={loadingEdit} className="p-button-rounded p-button-success mr-2" onClick={() => editAffiliate(row)} />
            <Button icon="pi pi-trash" className="p-button-rounded p-button-danger" onClick={() => confirmDeleteAffiliate(row)} />
        </>
    );

    // ==================== HEADER DA TABELA ====================
    const header = (
        <div className="flex flex-column md:flex-row md:align-items-center justify-content-between">
            <span className="p-input-icon-left w-full md:w-auto">
                <i className="pi pi-search" />
                <InputText type="search" onInput={(e) => setGlobalFilter(e.target.value)} placeholder="Search..." className="w-full lg:w-auto" />
            </span>
            <div className="mt-3 md:mt-0 flex justify-content-end">
                <Button icon="pi pi-plus" className="mr-2 p-button-rounded" onClick={openNew} tooltip="New" />
                <Button icon="pi pi-trash" className="p-button-danger mr-2 p-button-rounded" onClick={confirmDeleteSelected} disabled={!selectedAffiliates || !selectedAffiliates.length} tooltip="Delete" />
                <Button icon="pi pi-upload" className="p-button-help p-button-rounded" onClick={() => dt.current.exportCSV()} tooltip="Export" />
            </div>
        </div>
    );

    // ==================== FOOTERS DOS DIALOGS INTERNOS ====================
    const deleteAffiliateDialogFooter = (
        <>
            <Button label="No" icon="pi pi-times" className="p-button-text" onClick={() => setDeleteAffiliateDialog(false)} />
            <Button label="Yes" icon="pi pi-check" className="p-button-text" onClick={deleteAffiliate} />
        </>
    );
    const deleteAffiliatesDialogFooter = (
        <>
            <Button label="No" icon="pi pi-times" className="p-button-text" onClick={() => setDeleteAffiliatesDialog(false)} />
            <Button label="Yes" icon="pi pi-check" className="p-button-text" onClick={deleteSelectedAffiliates} />
        </>
    );
    const insertAnnuityDialogFooter = (
        <>
            <Button label="Nao" icon="pi pi-times" className="p-button-text" onClick={hideInsertAnnuityDialog} />
            <Button label="Sim" icon="pi pi-check" className="p-button-text" onClick={addAnnuity} />
        </>
    );
    const editAnnuityDialogFooter = (
        <>
            <Button label="Nao" icon="pi pi-times" className="p-button-text" onClick={hideEditAnnuityDialog} />
            <Button label="Sim" icon="pi pi-check" className="p-button-text" onClick={updateAnnuity} />
        </>
    );
    const deleteAnnuityDialogFooter = (
        <>
            <Button label="No" icon="pi pi-times" className="p-button-text" onClick={hideDeleteAnnuityDialog} />
            <Button label="Yes" icon="pi pi-check" className="p-button-text" onClick={deleteAnnuity} />
        </>
    );
    const insertDanExamDialogFooter = (
        <>
            <Button label="Nao" icon="pi pi-times" className="p-button-text" onClick={hideInsertDanExamDialog} />
            <Button label="Sim" icon="pi pi-check" className="p-button-text" onClick={addDanExam} />
        </>
    );
    const editDanExamDialogFooter = (
        <>
            <Button label="Nao" icon="pi pi-times" className="p-button-text" onClick={hideEditDanExamDialog} />
            <Button label="Sim" icon="pi pi-check" className="p-button-text" onClick={updateDanExam} />
        </>
    );
    const deleteDanExamDialogFooter = (
        <>
            <Button label="No" icon="pi pi-times" className="p-button-text" onClick={hideDeleteDanExamDialog} />
            <Button label="Yes" icon="pi pi-check" className="p-button-text" onClick={deleteDanExam} />
        </>
    );
    const insertJudgeLicenseDialogFooter = (
        <>
            <Button label="Nao" icon="pi pi-times" className="p-button-text" onClick={hideInsertJudgeLicenseDialog} />
            <Button label="Sim" icon="pi pi-check" className="p-button-text" onClick={addJudgeLicense} />
        </>
    );
    const editJudgeLicenseDialogFooter = (
        <>
            <Button label="Nao" icon="pi pi-times" className="p-button-text" onClick={hideEditJudgeLicenseDialog} />
            <Button label="Sim" icon="pi pi-check" className="p-button-text" onClick={updateJudgeLicense} />
        </>
    );
    const deleteJudgeLicenseDialogFooter = (
        <>
            <Button label="No" icon="pi pi-times" className="p-button-text" onClick={hideDeleteJudgeLicenseDialog} />
            <Button label="Yes" icon="pi pi-check" className="p-button-text" onClick={deleteJudgeLicense} />
        </>
    );
    const insertInstructorLicenseDialogFooter = (
        <>
            <Button label="Nao" icon="pi pi-times" className="p-button-text" onClick={hideInsertInstructorLicenseDialog} />
            <Button label="Sim" icon="pi pi-check" className="p-button-text" onClick={addInstructorLicense} />
        </>
    );
    const editInstructorLicenseDialogFooter = (
        <>
            <Button label="Nao" icon="pi pi-times" className="p-button-text" onClick={hideEditInstructorLicenseDialog} />
            <Button label="Sim" icon="pi pi-check" className="p-button-text" onClick={updateInstructorLicense} />
        </>
    );
    const deleteInstructorLicenseDialogFooter = (
        <>
            <Button label="No" icon="pi pi-times" className="p-button-text" onClick={hideDeleteInstructorLicenseDialog} />
            <Button label="Yes" icon="pi pi-check" className="p-button-text" onClick={deleteInstructorLicense} />
        </>
    );
    const insertExaminerLicenseDialogFooter = (
        <>
            <Button label="Nao" icon="pi pi-times" className="p-button-text" onClick={hideInsertExaminerLicenseDialog} />
            <Button label="Sim" icon="pi pi-check" className="p-button-text" onClick={addExaminerLicense} />
        </>
    );
    const editExaminerLicenseDialogFooter = (
        <>
            <Button label="Nao" icon="pi pi-times" className="p-button-text" onClick={hideEditExaminerLicenseDialog} />
            <Button label="Sim" icon="pi pi-check" className="p-button-text" onClick={updateExaminerLicense} />
        </>
    );
    const deleteExaminerLicenseDialogFooter = (
        <>
            <Button label="No" icon="pi pi-times" className="p-button-text" onClick={hideDeleteExaminerLicenseDialog} />
            <Button label="Yes" icon="pi pi-check" className="p-button-text" onClick={deleteExaminerLicense} />
        </>
    );

    // ==================== SUBCOMPONENTE: FORM DE LICENSE ====================
    // Reutilizado nos 6 dialogs (insert/edit x judge/instructor/examiner)
    // ==================== RENDER ====================
    return (
        <div className="datatable-crud-demo surface-card p-4 border-round shadow-2">
            <Toast ref={toast} />

            {/* Título + UI de busca opcional */}
            <div className="text-3xl text-800 font-bold mb-4">
                {t(titleKey)}

                {/* Input + botão Buscar (por nome ou JKA ID) */}
                {(searchByName || searchByJkaId) && (
                    <span className="ml-3 inline-flex gap-2 align-items-center" style={{ verticalAlign: 'middle' }}>
                        <InputText
                            value={searchValue}
                            onChange={(e) => setSearchValue(e.target.value)}
                            onKeyPress={handleSearchKeyPress}
                            placeholder={searchByJkaId ? 'Digite o JKA ID (ex: FL-12345)' : 'Digite o nome'}
                            className="p-inputtext-sm"
                            style={{ fontSize: '1rem', fontWeight: 'normal' }}
                        />
                        <Button
                            onClick={searchByJkaId ? handleSearchByJkaId : handleSearchByName}
                            label="Buscar"
                            icon="pi pi-search"
                            className="p-button-sm"
                        />
                    </span>
                )}

                {/* Dropdown de estado */}
                {filterByState && (
                    <span className="ml-3" style={{ verticalAlign: 'middle' }}>
                        <Dropdown
                            value={selectedFilterState}
                            options={states}
                            onChange={handleFilterStateChange}
                            optionLabel="name"
                            placeholder="Selecione um estado"
                            className="p-inputtext-sm"
                            style={{ fontSize: '1rem', fontWeight: 'normal' }}
                        />
                    </span>
                )}
            </div>

            <DataTable ref={dt} value={affiliates} selection={selectedAffiliates}
                onSelectionChange={(e) => setSelectedAffiliates(e.value)}
                dataKey="id" paginator rows={10} rowsPerPageOptions={[5, 10, 25]}
                paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                currentPageReportTemplate={t('currentPageReportTemplateAffiliates')}
                globalFilter={globalFilter} header={header} responsiveLayout="scroll">
                <Column selectionMode="multiple" headerStyle={{ width: '3rem' }} exportable={false} />
                <Column field="id" header="ID" sortable style={{ minWidth: '5rem' }} />
                <Column field="name" header={t('msg.jkaName')} sortable style={{ minWidth: '16rem' }} />
                <Column field="state.name" header={t('msg.jkaState')} sortable style={{ minWidth: '5rem' }} />
                <Column field="gym.name" header={t('msg.jkaGym')} sortable style={{ minWidth: '20rem' }} />
                <Column field="grade.level" header={t('msg.jkaGrade')} sortable style={{ minWidth: '5rem' }} />
                <Column field="status" header={t('msg.jkaAnnuity')} body={statusBodyTemplate} sortable style={{ minWidth: '5rem' }} />
                <Column field="active" header={t('msg.jkaActive')} body={activeBodyTemplate} sortable style={{ minWidth: '5rem' }} />
                <Column field="contacts.mobile1" header={t('msg.jkaMobile')} sortable style={{ minWidth: '5rem' }}
                    body={(row) => phoneBodyTemplate(row.contacts?.mobile1)} />
                <Column field="contacts.phone1" header={t('msg.jkaPhone')} sortable style={{ minWidth: '5rem' }}
                    body={(row) => phoneBodyTemplate(row.contacts?.phone1)} />
                <Column field="affiliateAddress.street" header={t('msg.jkaStreetAddress')} sortable style={{ minWidth: '16rem' }} />
                <Column field="affiliateAddress.streetNumber" header={t('msg.jkaStreetAddressNumber')} sortable style={{ minWidth: '5rem' }} />
                <Column field="birthDate" header={t('msg.jkaBirthDate')} sortable style={{ minWidth: '5rem' }} />
                <Column body={actionBodyTemplate} exportable={false} style={{ minWidth: '8rem' }} />
            </DataTable>

            {/* Dialog principal DECOMPOSTO em tabs */}
            <AffiliateFormDialog
                visible={affiliateDialog}
                onHide={hideDialog}
                onSave={saveAffiliate}
                affiliate={affiliate}
                onInputChange={onInputChange}
                submitted={submitted}
                activeIndex={activeIndex}
                setActiveIndex={setActiveIndex}
                dojos={dojos}
                states={states}
                grades={grades}
                selectedGym={selectedDojo}
                selectedState={selectedState}
                selectedGrade={selectedGrade}
                selectedPaid={selectedPaid}
                selectedActive={selectedActive}
                onGymChange={onDojoChange}
                onStateChange={onStateChange}
                onGradeChange={onGradeChange}
                onPaidChange={onPaidChange}
                onActiveChange={onActiveChange}
                statusPayment={statusPayment}
                statusActive={statusActive}
                apiError={affiliateApiError}
                onAddAnnuity={showAnnuityDialog}
                onEditAnnuity={editAnnuity}
                onDeleteAnnuity={confirmDeleteAnnuity}
                onAddDanExam={showDanExamDialog}
                onEditDanExam={editDanExam}
                onDeleteDanExam={confirmDeleteDanExam}
                onAddInstructorLicense={showInstructorLicenseDialog}
                onEditInstructorLicense={editInstructorLicense}
                onDeleteInstructorLicense={confirmDeleteInstructorLicense}
                onAddExaminerLicense={showExaminerLicenseDialog}
                onEditExaminerLicense={editExaminerLicense}
                onDeleteExaminerLicense={confirmDeleteExaminerLicense}
                onAddJudgeLicense={showJudgeLicenseDialog}
                onEditJudgeLicense={editJudgeLicense}
                onDeleteJudgeLicense={confirmDeleteJudgeLicense}
            />

            {/* ============== DIALOGS SECUNDARIOS (Annuity / DanExam / Licenses) ============== */}

            {/* ANNUITY - insert */}
            <Dialog visible={annuityDialog} style={{ width: '650px' }} header="Inserir anuidade" modal className="p-fluid" footer={insertAnnuityDialogFooter} onHide={hideInsertAnnuityDialog}>
                <div className="field">
                    <Fieldset>
                        <div className="formgrid grid">
                            <div className="field input text col-2">
                                <label htmlFor="annuityYear">{t('msg.jkaYear')}</label>
                                <InputMask id="annuityYear" mask="9999" placeholder="2024" value={annuityYear} onChange={(e) => setAnnuityYear(e.target.value)} />
                            </div>
                            <div className="field input text col-3">
                                <label htmlFor="paymentDate">{t('msg.jkaAnnuityPaymentDate')}</label>
                                <InputMask id="paymentDate" mask="99/99/9999" placeholder="31/12/2024" value={paymentDate} onChange={(e) => setPaymentDate(e.target.value)} />
                            </div>
                            <div className="field input text col-3">
                                <label htmlFor="value">{t('msg.jkaValue')}</label>
                                <InputText id="value" value={value} onChange={(e) => setValue(e.target.value)} />
                            </div>
                            <div className="field input text col-3">
                                <label htmlFor="jkabrCardDate">{t('msg.jkaCardIssueDate')}</label>
                                <InputMask id="jkabrCardDate" mask="99/99/9999" placeholder="31/12/2024" value={jkabrCardDate} onChange={(e) => setJkabrCardDate(e.target.value)} />
                            </div>
                        </div>
                    </Fieldset>
                </div>
            </Dialog>

            {/* ANNUITY - edit */}
            <Dialog visible={editAnnuityDialog} style={{ width: '650px' }} header="Editar anuidade" modal className="p-fluid" footer={editAnnuityDialogFooter} onHide={hideEditAnnuityDialog}>
                <div className="field">
                    <Fieldset>
                        <div className="formgrid grid">
                            <div className="field input text col-2">
                                <label htmlFor="annuityYear">{t('msg.jkaYear')}</label>
                                <InputMask id="annuityYear" mask="9999" value={annuityYear} onChange={(e) => setAnnuityYear(e.target.value)} />
                            </div>
                            <div className="field input text col-3">
                                <label htmlFor="paymentDate">{t('msg.jkaAnnuityPaymentDate')}</label>
                                <InputMask id="paymentDate" mask="99/99/9999" value={paymentDate} onChange={(e) => setPaymentDate(e.target.value)} />
                            </div>
                            <div className="field input text col-3">
                                <label htmlFor="value">{t('msg.jkaValue')}</label>
                                <InputText id="value" value={value} onChange={(e) => setValue(e.target.value)} />
                            </div>
                            <div className="field input text col-3">
                                <label htmlFor="jkabrCardDate">{t('msg.jkaCardIssueDate')}</label>
                                <InputMask id="jkabrCardDate" mask="99/99/9999" value={jkabrCardDate} onChange={(e) => setJkabrCardDate(e.target.value)} />
                            </div>
                        </div>
                    </Fieldset>
                </div>
            </Dialog>

            {/* DAN EXAM - insert */}
            <Dialog visible={danExamDialog} style={{ width: '650px' }} header="Inserir exame de dan" modal className="p-fluid" footer={insertDanExamDialogFooter} onHide={hideInsertDanExamDialog}>
                <div className="field">
                    <Fieldset>
                        <div className="formgrid grid license">
                            <div className="field input text col-2">
                                <label htmlFor="danId">{t('msg.jkaDan')}</label>
                                <InputMask id="danId" mask="9" placeholder="1" value={danExamDanId} onChange={(e) => setDanExamDanId(e.target.value)} />
                            </div>
                            <div className="field input text col-3">
                                <label htmlFor="danExamRecordDate">{t('msg.jkaExamDate')}</label>
                                <InputMask id="danExamRecordDate" mask="99/99/9999" placeholder="31/02/2023" value={danExamRecordDate} onChange={(e) => setDanExamRecordDate(e.target.value)} />
                            </div>
                            <div className="field input text col-4">
                                <label htmlFor="danExamRecord">{t('msg.jkaLicenseRegistrationId')}</label>
                                <InputText id="danExamRecord" value={danExamRecord} onChange={(e) => setDanExamRecord(e.target.value)} />
                            </div>
                        </div>
                    </Fieldset>
                </div>
            </Dialog>

            {/* DAN EXAM - edit */}
            <Dialog visible={editDanExamDialog} style={{ width: '650px' }} header="Editar exame de dan" modal className="p-fluid" footer={editDanExamDialogFooter} onHide={hideEditDanExamDialog}>
                <div className="field">
                    <Fieldset>
                        <div className="formgrid grid license">
                            <div className="field input text col-2">
                                <label htmlFor="danId">{t('msg.jkaDan')}</label>
                                <InputMask id="danId" mask="9" value={danExamDanId} onChange={(e) => setDanExamDanId(e.target.value)} />
                            </div>
                            <div className="field input text col-3">
                                <label htmlFor="danExamRecordDate">{t('msg.jkaExamDate')}</label>
                                <InputMask id="danExamRecordDate" mask="99/99/9999" value={danExamRecordDate} onChange={(e) => setDanExamRecordDate(e.target.value)} />
                            </div>
                            <div className="field input text col-4">
                                <label htmlFor="danExamRecord">{t('msg.jkaLicenseRegistrationId')}</label>
                                <InputText id="danExamRecord" value={danExamRecord} onChange={(e) => setDanExamRecord(e.target.value)} />
                            </div>
                        </div>
                    </Fieldset>
                </div>
            </Dialog>

            {/* LICENSE - Judge insert */}
            <Dialog visible={judgeLicenseDialog} style={{ width: '650px' }} header="Inserir licenca de arbitragem" modal className="p-fluid" footer={insertJudgeLicenseDialogFooter} onHide={hideInsertJudgeLicenseDialog}>
                <LicenseFormFields
                    t={t}
                    licenseClass={licenseClass}
                    setLicenseClass={setLicenseClass}
                    licenseRegistrationId={licenseRegistrationId}
                    setLicenseRegistrationId={setLicenseRegistrationId}
                    licenseAquisitionYear={licenseAquisitionYear}
                    setLicenseAquisitionYear={setLicenseAquisitionYear}
                    licenseValid={licenseValid}
                    setLicenseValid={setLicenseValid}
                />
            </Dialog>

            {/* LICENSE - Judge edit */}
            <Dialog visible={editJudgeLicenseDialog} style={{ width: '650px' }} header="Editar licenca de arbitragem" modal className="p-fluid" footer={editJudgeLicenseDialogFooter} onHide={hideEditJudgeLicenseDialog}>
                <LicenseFormFields
                    t={t}
                    licenseClass={licenseClass}
                    setLicenseClass={setLicenseClass}
                    licenseRegistrationId={licenseRegistrationId}
                    setLicenseRegistrationId={setLicenseRegistrationId}
                    licenseAquisitionYear={licenseAquisitionYear}
                    setLicenseAquisitionYear={setLicenseAquisitionYear}
                    licenseValid={licenseValid}
                    setLicenseValid={setLicenseValid}
                />
            </Dialog>

            {/* LICENSE - Instructor insert */}
            <Dialog visible={instructorLicenseDialog} style={{ width: '650px' }} header="Inserir licenca de instrutor" modal className="p-fluid" footer={insertInstructorLicenseDialogFooter} onHide={hideInsertInstructorLicenseDialog}>
                <LicenseFormFields
                    t={t}
                    licenseClass={licenseClass}
                    setLicenseClass={setLicenseClass}
                    licenseRegistrationId={licenseRegistrationId}
                    setLicenseRegistrationId={setLicenseRegistrationId}
                    licenseAquisitionYear={licenseAquisitionYear}
                    setLicenseAquisitionYear={setLicenseAquisitionYear}
                    licenseValid={licenseValid}
                    setLicenseValid={setLicenseValid}
                />
            </Dialog>

            {/* LICENSE - Instructor edit */}
            <Dialog visible={editInstructorLicenseDialog} style={{ width: '650px' }} header="Editar licenca de instrutor" modal className="p-fluid" footer={editInstructorLicenseDialogFooter} onHide={hideEditInstructorLicenseDialog}>
                <LicenseFormFields
                    t={t}
                    licenseClass={licenseClass}
                    setLicenseClass={setLicenseClass}
                    licenseRegistrationId={licenseRegistrationId}
                    setLicenseRegistrationId={setLicenseRegistrationId}
                    licenseAquisitionYear={licenseAquisitionYear}
                    setLicenseAquisitionYear={setLicenseAquisitionYear}
                    licenseValid={licenseValid}
                    setLicenseValid={setLicenseValid}
                />
            </Dialog>

            {/* LICENSE - Examiner insert */}
            <Dialog visible={examinerLicenseDialog} style={{ width: '650px' }} header="Inserir licenca de examinador" modal className="p-fluid" footer={insertExaminerLicenseDialogFooter} onHide={hideInsertExaminerLicenseDialog}>
                <LicenseFormFields
                    t={t}
                    licenseClass={licenseClass}
                    setLicenseClass={setLicenseClass}
                    licenseRegistrationId={licenseRegistrationId}
                    setLicenseRegistrationId={setLicenseRegistrationId}
                    licenseAquisitionYear={licenseAquisitionYear}
                    setLicenseAquisitionYear={setLicenseAquisitionYear}
                    licenseValid={licenseValid}
                    setLicenseValid={setLicenseValid}
                />
            </Dialog>

            {/* LICENSE - Examiner edit */}
            <Dialog visible={editExaminerLicenseDialog} style={{ width: '650px' }} header="Editar licenca de examinador" modal className="p-fluid" footer={editExaminerLicenseDialogFooter} onHide={hideEditExaminerLicenseDialog}>
                <LicenseFormFields
                    t={t}
                    licenseClass={licenseClass}
                    setLicenseClass={setLicenseClass}
                    licenseRegistrationId={licenseRegistrationId}
                    setLicenseRegistrationId={setLicenseRegistrationId}
                    licenseAquisitionYear={licenseAquisitionYear}
                    setLicenseAquisitionYear={setLicenseAquisitionYear}
                    licenseValid={licenseValid}
                    setLicenseValid={setLicenseValid}
                />
            </Dialog>

            {/* ============== DELETE CONFIRMATIONS ============== */}
            <Dialog visible={deleteAffiliateDialog} style={{ width: '450px' }} header="Confirm" modal footer={deleteAffiliateDialogFooter} onHide={() => setDeleteAffiliateDialog(false)}>
                <div className="flex align-items-center justify-content-center">
                    <i className="pi pi-exclamation-triangle mr-3" style={{ fontSize: '2rem' }} />
                    {affiliate && <span>Are you sure you want to delete <b>{affiliate.name}</b>?</span>}
                </div>
            </Dialog>

            <Dialog visible={deleteAffiliatesDialog} style={{ width: '450px' }} header="Confirm" modal footer={deleteAffiliatesDialogFooter} onHide={() => setDeleteAffiliatesDialog(false)}>
                <div className="flex align-items-center justify-content-center">
                    <i className="pi pi-exclamation-triangle mr-3" style={{ fontSize: '2rem' }} />
                    <span>Are you sure you want to delete the selected affiliates?</span>
                </div>
            </Dialog>

            <Dialog visible={deleteAnnuityDialog} style={{ width: '450px' }} header="Confirm" modal footer={deleteAnnuityDialogFooter} onHide={hideDeleteAnnuityDialog}>
                <div className="flex align-items-center justify-content-center">
                    <i className="pi pi-exclamation-triangle mr-3" style={{ fontSize: '2rem' }} />
                    <span>Are you sure you want to delete this annuity?</span>
                </div>
            </Dialog>

            <Dialog visible={deleteDanExamDialog} style={{ width: '450px' }} header="Confirm" modal footer={deleteDanExamDialogFooter} onHide={hideDeleteDanExamDialog}>
                <div className="flex align-items-center justify-content-center">
                    <i className="pi pi-exclamation-triangle mr-3" style={{ fontSize: '2rem' }} />
                    <span>Are you sure you want to delete this dan exam?</span>
                </div>
            </Dialog>

            <Dialog visible={deleteJudgeLicenseDialog} style={{ width: '450px' }} header="Confirm" modal footer={deleteJudgeLicenseDialogFooter} onHide={hideDeleteJudgeLicenseDialog}>
                <div className="flex align-items-center justify-content-center">
                    <i className="pi pi-exclamation-triangle mr-3" style={{ fontSize: '2rem' }} />
                    <span>Are you sure you want to delete this judge license?</span>
                </div>
            </Dialog>

            <Dialog visible={deleteInstructorLicenseDialog} style={{ width: '450px' }} header="Confirm" modal footer={deleteInstructorLicenseDialogFooter} onHide={hideDeleteInstructorLicenseDialog}>
                <div className="flex align-items-center justify-content-center">
                    <i className="pi pi-exclamation-triangle mr-3" style={{ fontSize: '2rem' }} />
                    <span>Are you sure you want to delete this instructor license?</span>
                </div>
            </Dialog>

            <Dialog visible={deleteExaminerLicenseDialog} style={{ width: '450px' }} header="Confirm" modal footer={deleteExaminerLicenseDialogFooter} onHide={hideDeleteExaminerLicenseDialog}>
                <div className="flex align-items-center justify-content-center">
                    <i className="pi pi-exclamation-triangle mr-3" style={{ fontSize: '2rem' }} />
                    <span>Are you sure you want to delete this examiner license?</span>
                </div>
            </Dialog>
        </div>
    );
}

export default AffiliateList;
